// The conversation with Dyia as the web app keeps it (doc 13 §2). Pure helpers, no React.
import { ASSISTANT_MAX_MESSAGES, type AssistantBlock, type AssistantMessage, type AssistantStreamEvent } from '@twinlink/shared';

export type ActionBlock = Extract<AssistantBlock, { type: 'action' }>;

/** Where a card of changes stands. `done` counts the steps already made. */
export interface ActionState {
  status: 'waiting' | 'running' | 'done' | 'failed' | 'cancelled';
  done: number;
  error?: string;
}
export const WAITING: ActionState = { status: 'waiting', done: 0 };

export interface Turn {
  id: string;
  role: 'user' | 'assistant';
  /** What the user typed. */
  text: string;
  /** What Dyia answered. */
  blocks: AssistantBlock[];
  /** The answer is still being written. */
  pending?: boolean;
  /** Set when the request failed: the turn is shown but not sent back as history. */
  error?: string;
  /** State of the cards of changes of this answer, by position of the block. */
  actions?: Record<number, ActionState>;
}

/** Earlier exchanges are sent back shortened: Dyia needs the thread, not every word of it. */
const HISTORY_TURNS = 12;
const HISTORY_TEXT = 1200;

/** How a card of changes ended: Dyia must know what was really done before it goes on. */
function outcome(b: ActionBlock, state: ActionState): string {
  const what = b.steps.map((s) => s.label).join('; ');
  if (state.status === 'done') return `(prepared: ${what} — the user confirmed and it was done)`;
  if (state.status === 'cancelled') return `(prepared: ${what} — the user cancelled, nothing was done)`;
  if (state.status === 'failed') return `(prepared: ${what} — the user confirmed; ${state.done} of ${b.steps.length} done, then it failed: ${state.error ?? 'error'})`;
  return `(prepared: ${what} — waiting for the user to confirm, nothing is done yet)`;
}

/** What Dyia did in a block, as it reads it back at the next question. */
function note(b: AssistantBlock, state: ActionState | undefined): string {
  if (b.type === 'text') return b.text;
  if (b.type === 'navigate') return `(opened the page "${b.label}")`;
  if (b.type === 'chart') return `(showed a chart "${b.title}" of ${b.series.map((s) => s.name).join(', ')})`;
  if (b.type === 'stats') return `(showed the figures "${b.title}" of ${b.rows.map((r) => r.name).join(', ')})`;
  return outcome(b, state ?? WAITING);
}

/** The messages sent with a question: the last exchanges as text, the question last. */
export function historyOf(turns: Turn[]): AssistantMessage[] {
  const messages = turns.flatMap((t): AssistantMessage[] => {
    if (t.error || t.pending) return [];
    const text = (t.role === 'user' ? t.text : t.blocks.map((b, i) => note(b, t.actions?.[i])).join('\n')).trim().slice(0, t.role === 'user' ? 4000 : HISTORY_TEXT);
    return text ? [{ role: t.role, text }] : [];
  });
  return messages.slice(-Math.min(HISTORY_TURNS, ASSISTANT_MAX_MESSAGES));
}

/** The answer as it stands after one more event of the stream. */
export function applyEvent(blocks: AssistantBlock[], event: AssistantStreamEvent): AssistantBlock[] {
  if (event.t === 'done') return event.blocks;
  if (event.t === 'block') return [...blocks, event.block];
  if (event.t !== 'text') return blocks;
  const last = blocks[blocks.length - 1];
  // Text goes on in the paragraph being written; after a chart or a table it starts a new one.
  if (last?.type === 'text') return [...blocks.slice(0, -1), { type: 'text', text: last.text + event.d }];
  return [...blocks, { type: 'text', text: event.d.replace(/^\s+/, '') }];
}

/** Path of a `navigate` block: relative to the organization unless it starts with a slash. */
export function targetPath(orgId: string, path: string): string {
  if (path.startsWith('/')) return path;
  return path ? `/o/${orgId}/${path}` : `/o/${orgId}`;
}

/** Replaces `{{n}}` by the id step n returned, in a path or anywhere in a body. */
export function fill<T>(value: T, ids: string[]): T {
  if (value === undefined) return value;
  return JSON.parse(JSON.stringify(value).replace(/\{\{(\d+)\}\}/g, (_, n: string) => ids[Number(n) - 1] ?? '')) as T;
}

/** The same trend in the Data Explorer: a link that can be kept or sent to a colleague. */
export function explorerPath(orgId: string, block: Extract<AssistantBlock, { type: 'chart' }>): string {
  const params = new URLSearchParams({ s: block.series.map((s) => s.signalId).join(','), from: block.range.from, to: block.range.to });
  return `/o/${orgId}/explorer?${params.toString()}`;
}
